import React, { useState, useEffect, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";

// ✅ FIX #3: Required CSS imports for text selection and annotation layers to work
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

import {
  Plus,
  Minus,
  ChevronLeft,
  ChevronRight,
  Download,
  Printer,
  MoreVertical,
} from "lucide-react";

// ✅ FIX #4: Use unpkg CDN for worker — more reliable across bundlers
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const CustomFileViewer = ({ fileType, fileUrl }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const pageRefs = useRef([]);
  const containerRef = useRef(null);

  // ✅ FIX #5: Revoke previous blob URL on fileUrl change, not just on unmount
  useEffect(() => {
    if (!fileUrl) return;

    let objectUrl;

    fetch(fileUrl)
      .then((res) => res.blob())
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setPdfBlob(objectUrl);
      })
      .catch((err) => console.error("PDF fetch error:", err));

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileUrl]);

  // IntersectionObserver to sync page number with scroll
  useEffect(() => {
    if (!numPages || !pageRefs.current.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visiblePage = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (visiblePage) {
          const pageIndex = Number(visiblePage.target.dataset.pageIndex);
          setPageNumber(pageIndex + 1);
        }
      },
      { root: containerRef.current, threshold: 0.6 }
    );

    pageRefs.current.forEach((ref) => ref && observer.observe(ref));

    return () => observer.disconnect();
  }, [numPages]);

  const btnStyle = {
    background: "transparent",
    border: "none",
    color: "white",
    cursor: "pointer",
    padding: "4px",
    borderRadius: "4px",
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    // ✅ FIX #6: Clear stale refs from previous document before loading new one
    pageRefs.current = [];
    setNumPages(numPages);
    setPageNumber(1);
  };

  const goPrevPage = () => {
    if (pageNumber > 1) {
      const newPage = pageNumber - 1;
      setPageNumber(newPage);
      pageRefs.current[newPage - 1]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  };

  const goNextPage = () => {
    if (pageNumber < numPages) {
      const newPage = pageNumber + 1;
      setPageNumber(newPage);
      pageRefs.current[newPage - 1]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  };

  const handleDownload = () => {
    if (pdfBlob) {
      const link = document.createElement("a");
      link.href = pdfBlob;
      link.setAttribute("download", fileUrl?.split("/").pop() || "document.pdf");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePrint = () => {
    if (pdfBlob) {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = pdfBlob;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      };
    }
  };

  if (!fileUrl) return <div>Loading file...</div>;

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        backgroundColor: "#000",
        color: "white",
        padding: "10px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {fileType === "pdf" ? (
        <>
          {/* Toolbar */}
          <div
            style={{
              backgroundColor: "#2c2c2c",
              padding: "6px 12px",
              display: "flex",
              // ✅ FIX #2: "auto" is not a valid alignItems value
              alignItems: "center",
              justifyContent: "space-between",
              color: "#fff",
              fontSize: "14px",
            }}
          >
            {/* Left: File name */}
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontWeight: "500" }}>
                {fileUrl?.split("/").pop()}
              </span>
            </div>

            {/* Center: Navigation + Zoom */}
            <div
              style={{
                display: "flex",
                // ✅ FIX #2: "auto" is not a valid alignItems value
                alignItems: "center",
                gap: "12px",
              }}
            >
              <button
                onClick={goPrevPage}
                style={btnStyle}
                disabled={pageNumber <= 1}
                title="Previous Page"
              >
                <ChevronLeft size={18} />
              </button>
              <span
                style={{
                  padding: "2px 6px",
                  background: "#111",
                  borderRadius: "4px",
                  fontSize: "13px",
                }}
              >
                {pageNumber}
              </span>
              <span>/ {numPages}</span>
              <button
                onClick={goNextPage}
                style={btnStyle}
                disabled={pageNumber >= numPages}
                title="Next Page"
              >
                <ChevronRight size={18} />
              </button>

              <div style={{ width: "1px", height: "20px", background: "#666" }} />

              <button
                onClick={() => setScale((s) => Math.max(0.2, s - 0.2))}
                style={btnStyle}
                title="Zoom Out"
              >
                <Minus size={16} />
              </button>
              <span
                style={{
                  padding: "2px 6px",
                  background: "#111",
                  borderRadius: "4px",
                  fontSize: "13px",
                }}
              >
                {(scale * 100).toFixed(0)}%
              </span>
              <button
                onClick={() => setScale((s) => s + 0.2)}
                style={btnStyle}
                title="Zoom In"
              >
                <Plus size={16} />
              </button>
            </div>

            {/* Right: Actions */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                position: "relative",
              }}
            >
              <button onClick={handleDownload} style={btnStyle} title="Download">
                <Download size={18} />
              </button>
              <button onClick={handlePrint} style={btnStyle} title="Print">
                <Printer size={18} />
              </button>
              <button
                onClick={() => setShowMenu((v) => !v)}
                style={btnStyle}
                title="More"
              >
                <MoreVertical size={18} />
              </button>
              {showMenu && (
                <div
                  style={{
                    position: "absolute",
                    top: "30px",
                    right: "0",
                    background: "#333",
                    padding: "8px",
                    borderRadius: "6px",
                    boxShadow: "0px 4px 6px rgba(0,0,0,0.3)",
                    zIndex: 10,
                  }}
                >
                  <div style={{ padding: "4px 8px", cursor: "pointer" }}>
                    Rotate
                  </div>
                  <div style={{ padding: "4px 8px", cursor: "pointer" }}>
                    Properties
                  </div>
                  <div style={{ padding: "4px 8px", cursor: "pointer" }}>
                    Share
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* PDF Viewer */}
          <div
            id="pdf-scroll-container"
            ref={containerRef}
            style={{
              flex: 1,
              overflowX: "auto",
              overflowY: "auto",
              padding: "10px",
              display: "flex",
              flexDirection: "column",
              scrollbarColor: "#fff #000",
              scrollbarWidth: "thin",
            }}
          >
            <Document
              file={pdfBlob || fileUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={(err) => console.error("PDF load error:", err)}
            >
              {Array.from(new Array(numPages), (_, index) => (
                <div
                  key={`page_${index + 1}`}
                  ref={(el) => (pageRefs.current[index] = el)}
                  data-page-index={index}
                  style={{
                    marginBottom: "40px",
                    backgroundColor: "black",
                    display: "flex",
                    // ✅ FIX #1: Removed duplicate justifyContent key.
                    // Center at default zoom, left-align when zoomed in so
                    // the user can scroll horizontally to see the full page.
                    justifyContent: scale <= 1 ? "center" : "flex-start",
                  }}
                >
                  <Page
                    pageNumber={index + 1}
                    scale={scale}
                    renderAnnotationLayer={true}
                    // ✅ FIX #3: Keep text layer enabled — selection now works
                    // because TextLayer.css is imported above
                    renderTextLayer={true}
                  />
                </div>
              ))}
            </Document>
          </div>
        </>
      ) : (
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <a href={fileUrl} download style={{ color: "#fff" }}>
            <Download size={20} /> Download File
          </a>
        </div>
      )}
    </div>
  );
};

export default CustomFileViewer;